<template>
  <div class="van-cell calendars">
    <van-field
      readonly
      clickable
      :is-link="!disabled && !readonly"
      :value="displayValue"
      :name="name"
      :label="label"
      :label-width="labelWidth"
      :placeholder="placeholder"
      :required="required"
      :rules="rules"
      :disabled="disabled"
      @click="handField"
      center
    />
    <van-popup
      v-model="showPicker"
      closeable
      round
      :lock-scroll="false"
      close-icon-position="top-left"
      position="bottom"
      :style="{ height: '60%' }"
      @close="showPicker = false"
    >
      <div class="popup-pack">
        <div class="popup-title">
          <span>{{ placeholder || label }}</span>
        </div>
        <div class="popup-content">
          <div class="tree-list">
            <div 
              v-for="(item, index) in displayList" 
              :key="`${item.id}-${index}`"
              class="tree-item"
              :class="{ 
                'tree-item-selected': item.id === activeId, 
                'tree-item-expanded': item.expanded,
                'tree-item-has-children': item.hasChildren
              }"
              :style="{ paddingLeft: `${item.level * 12 + 12}px` }"
            >
              <div class="tree-item-content">
                <div 
                  v-if="item.hasChildren" 
                  class="tree-item-icon-wrapper"
                  @click.stop="toggleExpand(item.id)"
                >
                  <van-icon 
                    :name="item.expanded ? 'arrow-down' : 'arrow'" 
                    class="tree-item-icon"
                  />
                </div>
                <span 
                  class="tree-item-text" 
                  @click="handleItemClick(item)"
                >{{ item.text }}</span>
                <van-icon 
                  v-if="item.id === activeId" 
                  name="success" 
                  class="tree-item-check"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
export default {
  name: 'VcTreeSelect',
  props: {
    name: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    labelWidth: {
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
    rules: {
      type: [Array, Object],
      default: null
    },
    value: null,
    options: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showPicker: false,
      displayValue: '',
      treeData: [],
      activeId: null,
      mainActiveIndex: 0,
      // 用于存储原始的多级数据结构
      originalData: [],
      // 扁平化的显示列表（上下展开显示）
      displayList: [],
      // 展开状态映射
      expandedMap: {}
    };
  },
  watch: {
    options: {
      handler: function(newVal) {
        this.originalData = newVal || [];
        this.initTreeData(newVal || []);
      },
      immediate: true,
      deep: true
    },
    value: {
      handler: function(newVal) {
        this.updateDisplayValue(newVal);
        // 如果 value 变化，重新查找并设置 activeId
        if (newVal !== null && newVal !== undefined && newVal !== '') {
          this.$nextTick(() => {
            this.findActiveItem(newVal);
          });
        }
      },
      immediate: true
    }
  },
  methods: {
    initTreeData(options) {
      if (!options || options.length === 0) {
        this.treeData = [];
        this.displayValue = '';
        this.displayList = [];
        return;
      }

      // 刷新显示列表
      this.refreshDisplayList();
      
      // 如果有 value，找到对应的 activeId，并更新显示值
      if (this.value !== null && this.value !== undefined && this.value !== '') {
        this.findActiveItem(this.value);
        this.updateDisplayValue(this.value);
        // 自动展开到选中的项
        this.expandToSelected(this.value);
      }
    },
    refreshDisplayList() {
      // 刷新显示列表
      if (!this.originalData || this.originalData.length === 0) {
        this.displayList = [];
        return;
      }
      
      this.displayList = [];
      this.buildDisplayListRecursive(this.originalData, 0, true);
    },
    buildDisplayListRecursive(items, level = 0, parentExpanded = true) {
      // 递归构建扁平化的显示列表
      items.forEach((item) => {
        const itemId = item.value !== undefined && item.value !== null 
          ? item.value 
          : (item.id !== undefined && item.id !== null 
            ? item.id 
            : String(item.value || item.id || ''));
        const itemText = item.label || item.name || item.text || String(item.value || item.id || '');
        const hasChildren = !!(item.children && item.children.length > 0);
        const expanded = this.expandedMap[itemId] || false;
        
        const displayItem = {
          id: itemId,
          text: itemText,
          level: level,
          hasChildren: hasChildren,
          expanded: expanded,
          children: item.children || []
        };
        
        // 如果父级展开，则显示该项
        if (parentExpanded) {
          this.displayList.push(displayItem);
          
          // 如果有子级且该项展开，递归添加子级
          if (hasChildren && expanded) {
            this.buildDisplayListRecursive(item.children, level + 1, expanded);
          }
        }
      });
    },
    findActiveItem(value) {
      // 设置选中的项
      const targetValue = String(value);
      this.activeId = targetValue;
    },
    updateDisplayValue(value) {
      if (value === null || value === undefined || value === '') {
        this.displayValue = '';
        return;
      }

      // 在 options 中查找对应的 label
      const findLabel = (items, targetValue) => {
        for (const item of items) {
          if (item.value === targetValue || item.value === String(targetValue)) {
            return item.label || item.name || item.text;
          }
          if (item.children && item.children.length > 0) {
            const found = findLabel(item.children, targetValue);
            if (found) return found;
          }
        }
        return null;
      };

      const label = findLabel(this.options, value);
      this.displayValue = label || '';
    },
    handField() {
      if (!this.disabled && !this.readonly) {
        // 打开弹窗时，刷新显示列表
        this.refreshDisplayList();
        this.showPicker = true;
      }
    },
    handleItemClick(item) {
      // 检查是否有子节点，如果有则不允许选择（不能选父级）
      if (item.hasChildren) {
        // 可以添加提示或直接返回，不执行选择操作
        // this.$toast && this.$toast('父级节点不可选择，请选择子节点');
        return;
      }
      
      // 直接选中该项（不处理展开/折叠，展开/折叠由箭头单独处理）
      this.activeId = item.id;
      this.displayValue = item.text;
      this.showPicker = false;
      // 保持原有的 input 事件，传递 value
      this.$emit('input', item.id);
      // 新增 change 事件，传递包含 label 和 value 的对象
      this.$emit('change', {
        label: item.text,
        value: item.id
      });
    },
    toggleExpand(itemId) {
      // 切换展开/折叠状态
      this.$set(this.expandedMap, itemId, !this.expandedMap[itemId]);
      // 刷新显示列表
      this.refreshDisplayList();
    },
    expandToSelected(value) {
      // 自动展开到选中的项
      const targetValue = String(value);
      const findAndExpand = (items, level = 0) => {
        for (const item of items) {
          const itemId = String(item.value !== undefined && item.value !== null 
            ? item.value 
            : (item.id !== undefined && item.id !== null 
              ? item.id 
              : String(item.value || item.id || '')));
          
          if (itemId === targetValue) {
            // 找到目标项，展开所有父级
            return true;
          }
          
          if (item.children && item.children.length > 0) {
            if (findAndExpand(item.children, level + 1)) {
              // 子级中找到目标，展开当前项
              this.$set(this.expandedMap, itemId, true);
              return true;
            }
          }
        }
        return false;
      };
      
      if (this.originalData && this.originalData.length > 0) {
        findAndExpand(this.originalData);
        this.refreshDisplayList();
      }
    }
  }
};
</script>

<style lang="less" scoped>
.calendars {
  font-family: PingFangSC-Regular;
  padding: 0;
  flex-direction: column;
  .popup-pack {
    height: 100%;
    display: flex;
    flex-direction: column;
    .popup-title {
      height: 54px;
      width: 100%;
      text-align: center;
      line-height: 54px;
      border-bottom: 1px solid rgba(0, 0, 0, 0.12);
      font-family: PingFangSC-Medium;
      font-size: 18px;
      color: rgba(0, 0, 0, 0.85);
    }
    .popup-content {
      flex: 1;
      overflow-y: auto;
      
      .tree-list {
        width: 100%;
        
        .tree-item {
          border-bottom: 1px solid rgba(0, 0, 0, 0.06);
          
          .tree-item-content {
            display: flex;
            align-items: center;
            padding: 10px 16px;
            min-height: 40px;
            cursor: pointer;
            
            .tree-item-icon-wrapper {
              display: flex;
              align-items: center;
              justify-content: center;
              width: 24px;
              height: 24px;
              margin-right: 8px;
              cursor: pointer;
              
              .tree-item-icon {
                font-size: 14px;
                color: rgba(0, 0, 0, 0.45);
              }
            }
            
            .tree-item-text {
              flex: 1;
              font-size: 14px;
              color: rgba(0, 0, 0, 0.85);
            }
            
            .tree-item-check {
              font-size: 16px;
              color: #1989fa;
            }
          }
          
          &.tree-item-has-children {
            .tree-item-text {
              color: rgba(0, 0, 0, 0.45);
              cursor: not-allowed;
            }
            
            &:active {
              background-color: transparent;
            }
          }
          
          &.tree-item-selected {
            background-color: rgba(25, 137, 250, 0.05);
            
            .tree-item-text {
              color: #1989fa;
            }
          }
          
          &:active {
            background-color: rgba(0, 0, 0, 0.05);
          }
        }
      }
    }
  }
}
</style>

